import api from '.';
import logger from '@app/utils/logger';
import { AxiosError, AxiosProgressEvent, CancelTokenSource } from 'axios';
import { CustomError } from '@app/CustomError';
import { CustomErrorCode } from '@app/constants/error-codes';
import {
  DocumentResponse,
  AddDocumentRequest,
  AddDocumentResponse,
  GetDocumentsConfig,
} from '@app/types/document';

export const getDocuments = async (
  companyId: number,
  config?: GetDocumentsConfig,
  signal?: AbortSignal,
): Promise<DocumentResponse> => {
  try {
    const params = new URLSearchParams({
      ...(config?.type && { company_document_type_id: String(config.type) }),
      ...(config?.page && { page: String(config.page) }),
      ...(config?.size && { size: String(config.size) }),
      ...(config?.sortBy && { sort_by: config.sortBy }),
      ...(config?.orderBy && { order_by: config.orderBy }),
      ...(config?.statusIds && { status_id: config.statusIds.join(',') }),
      inactive_documents: 'true',
      voided:'false'
    });
    const { data } = await api.get(
      `/company-documents-service/companies/${companyId}/documents?${params.toString()}`,
      {
        signal,
      },
    );
    return data;
  } catch (error) {
    logger('error', 'at getDocuments while fetching documents', error);
    throw error;
  }
};

export const addDocument = async (
  companyId: number,
  requestBody: AddDocumentRequest,
): Promise<AddDocumentResponse> => {
  try {
    const { data } = await api.put(
      `/company-documents-service/companies/${companyId}/documents`,
      requestBody,
    );
    return data;
  } catch (error) {
    logger('error', 'at addDocument Fn', error);
    throw error;
  }
};

export const uploadFile = async (
  file: File,
  signedFormData: {
    url: string;
    fields: {
      'CONTENT-MD5': string;
      tagging: string;
      key: string;
      'x-amz-algorithm': string;
      'x-amz-credential': string;
      'x-amz-date': string;
      'x-amz-security-token': string;
      policy: string;
      'x-amz-signature': string;
      'Content-Type': string;
      acl: string;
    };
  },
  onProgressCallback?: (event: AxiosProgressEvent) => void,
  cancelTokenSource?: CancelTokenSource,
): Promise<string> => {
  try {
    const formData = new FormData();

    formData.append('CONTENT-MD5', signedFormData.fields['CONTENT-MD5']);
    formData.append('tagging', signedFormData.fields['tagging']);
    formData.append('key', signedFormData.fields['key']);
    formData.append('x-amz-algorithm', signedFormData.fields['x-amz-algorithm']);
    formData.append('x-amz-credential', signedFormData.fields['x-amz-credential']);
    formData.append('x-amz-date', signedFormData.fields['x-amz-date']);
    formData.append('x-amz-security-token', signedFormData.fields['x-amz-security-token']);
    formData.append('policy', signedFormData.fields['policy']);
    formData.append('x-amz-signature', signedFormData.fields['x-amz-signature']);
    formData.append('Content-Type', signedFormData.fields['Content-Type']);
    formData.append('acl', signedFormData.fields['acl']);
    formData.append('file', file);

    const response = await api.post(signedFormData.url, formData, {
      onUploadProgress: onProgressCallback,
      cancelToken: cancelTokenSource?.token,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    logger('error', 'at uploadFile Fn', error);
    if ((error as AxiosError).response?.status === 400) {
      throw new CustomError('at uploadFile Fn', CustomErrorCode.INVALID_PRESIGNED_URL);
    } else if (error instanceof AxiosError && error.code === 'ERR_CANCELED') {
      throw new CustomError('at uploadFile Fn', CustomErrorCode.UPLOAD_CANCELLED);
    } else {
      throw error;
    }
  }
};

export const getDocument = async (companyId: number, documentId: number): Promise<Blob> => {
  try {
    const { data } = await api.get(
      `/company-documents-service/companies/${companyId}/documents/${documentId}/download`,
      {
        responseType: 'blob',
      },
    );
    return data;
  } catch (error) {
    logger('error', 'at getDocument Fn', error);
    throw error;
  }
};
